import { Navigate, Outlet } from "react-router";
import { LoadingSpinner } from "@/shared/components/LoadingSpinner";
import { AUTH_STATUS, UserProvider, useUserContext } from "@/features/home/UserContext";

/**
 * AuthGuard — a UserContext-ből olvassa az auth státuszt.
 * UserProvider-en belül fut, ezért a context garantáltan elérhető.
 *
 * - "pending"         → spinner (GET /me folyamatban)
 * - "authenticated"   → <Outlet /> (rendereli a védett oldalt)
 * - "unauthenticated" → <Navigate to="/login" replace /> (visszairányít)
 */
function AuthGuard() {
  const { status } = useUserContext();

  if (status === AUTH_STATUS.PENDING) {
    return (
      <div className="bg-gradient-page flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (status === AUTH_STATUS.UNAUTHENTICATED) {
    return <Navigate replace to="/login" />;
  }

  return <Outlet />;
}

/**
 * ProtectedRoute — Angular AuthGuard ekvivalens React Routerben.
 *
 * Használat az App.tsx-ben:
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/home" element={<HomePage />} />
 * </Route>
 * ```
 *
 * Működés:
 * - UserProvider egyszer hívja a GET /v1/users/me végpontot.
 * - AuthGuard a context státusza alapján dönt a renderelésről.
 * - A védett oldalak (pl. HomePage) useMe()-vel olvasnak a context-ből,
 *   így megszűnik a duplikált API hívás.
 *
 * Megjegyzés: a return-url.service csak abszolút külső URL-t fogad el,
 * belső útvonalakat (/home) nem tárol — ezért a redirect egyszerűen
 * /login-ra mutat. Sikeres bejelentkezés után a LoginPage mindig
 * navigate("/home")-ra irányít, ami a kívánt viselkedés.
 */
export function ProtectedRoute() {
  return (
    <UserProvider>
      <AuthGuard />
    </UserProvider>
  );
}
